/**
 * «Войти через Telegram» — the official Telegram Login library (OpenID
 * Connect), https://core.telegram.org/bots/telegram-login.
 *
 * The button follows Telegram's own look (their blue, the paper plane); the
 * click opens Telegram's popup with a nonce the server issued. The result is
 * an id_token the server verifies — this component never decides who the
 * person is.
 *
 * The nonce is fetched before the click: a popup opened after an await is
 * blocked by browsers, so the click must open it synchronously.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../cn';

const SCRIPT_SRC = 'https://oauth.telegram.org/js/telegram-login.js?6';

interface TelegramLoginResult {
  id_token?: string;
  error?: string;
}
interface TelegramLoginApi {
  auth(
    options: { client_id: string | number; scope?: string[]; lang?: string; nonce?: string },
    callback: (r: TelegramLoginResult) => void,
  ): void;
}
declare global {
  interface Window {
    Telegram?: { Login?: TelegramLoginApi };
  }
}

let loading: Promise<void> | null = null;

/** Loads Telegram's library once per page. */
export function loadTelegramLogin(): Promise<void> {
  if (window.Telegram?.Login) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () =>
      window.Telegram?.Login ? resolve() : reject(new Error('Telegram Login not available'));
    s.onerror = () => {
      loading = null;
      s.remove();
      reject(new Error('Telegram Login not reachable'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export type TelegramLoginFailure = 'cancelled' | 'unavailable' | 'network' | 'failed';

export function TelegramLoginButton({
  clientId,
  getNonce,
  onToken,
  onFailure,
  label = 'Войти через Telegram',
  disabled,
  busy,
  className,
}: {
  clientId: string;
  /** A fresh server nonce (POST /auth/telegram/nonce). */
  getNonce: () => Promise<string>;
  onToken: (idToken: string, nonce: string) => void;
  onFailure: (kind: TelegramLoginFailure) => void;
  label?: string;
  disabled?: boolean;
  busy?: boolean;
  className?: string;
}) {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const nonce = useRef<{ value: string; at: number } | null>(null);
  // null: fetching; true: have one; false: the server did not answer.
  const [hasNonce, setHasNonce] = useState<boolean | null>(null);

  const prefetch = useCallback(() => {
    nonce.current = null;
    setHasNonce(null);
    getNonce()
      .then((value) => {
        nonce.current = { value, at: Date.now() };
        setHasNonce(true);
      })
      .catch(() => setHasNonce(false));
  }, [getNonce]);

  useEffect(() => {
    let alive = true;
    loadTelegramLogin()
      .then(() => alive && setReady(true))
      .catch(() => {
        if (!alive) return;
        setFailed(true);
      });
    prefetch();
    return () => {
      alive = false;
    };
  }, [prefetch]);

  const click = () => {
    const login = window.Telegram?.Login;
    if (!login) {
      onFailure('unavailable');
      return;
    }
    // Server nonces live 10 minutes; take a new one when this is close to it.
    const n = nonce.current;
    if (!n || Date.now() - n.at > 8 * 60_000) {
      prefetch();
      onFailure(n ? 'failed' : 'network');
      return;
    }
    nonce.current = null;
    login.auth(
      { client_id: clientId, scope: ['profile', 'phone'], lang: 'ru', nonce: n.value },
      (r) => {
        prefetch();
        if (r.id_token) onToken(r.id_token, n.value);
        else onFailure(r.error === 'popup_closed' ? 'cancelled' : 'failed');
      },
    );
  };

  if (failed) {
    return (
      <p
        role="alert"
        className={cn('rounded-xl bg-warning-soft px-4 py-3 text-base text-warning', className)}
      >
        Не удалось загрузить вход через Telegram. Проверьте интернет и обновите страницу.
      </p>
    );
  }
  const off = disabled || busy || !ready || hasNonce === null;
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-14 w-full items-center justify-center gap-3 rounded-xl bg-[#2aabee] px-5 text-lg font-semibold text-white transition-colors',
        'hover:bg-[#229ed9] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#229ed9] active:bg-[#1c8fc6]',
        off && 'cursor-default opacity-60 hover:bg-[#2aabee]',
        className,
      )}
      aria-busy={busy || !ready || hasNonce === null || undefined}
      disabled={off}
      onClick={click}
    >
      <TelegramGlyph className="size-6 shrink-0" />
      {busy ? 'Проверяем…' : label}
    </button>
  );
}

/** Telegram's paper plane. */
export function TelegramGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
      <path d="M21.4 4.1 2.9 11.2c-1.3.5-1.3 1.2-.2 1.5l4.7 1.5 1.8 5.6c.2.6.1.9.8.9.5 0 .7-.2 1-.5l2.3-2.2 4.8 3.5c.9.5 1.5.2 1.7-.8l3.1-14.7c.3-1.3-.5-1.9-1.5-1.4ZM8.2 13.9l10.2-6.4c.5-.3.9-.1.6.2l-8.7 7.9-.4 3.8-1.7-5.5Z" />
    </svg>
  );
}

/** Human text for a failed attempt. */
export function telegramFailureText(kind: TelegramLoginFailure): string | null {
  if (kind === 'cancelled') return null;
  if (kind === 'unavailable') return 'Telegram сейчас недоступен. Попробуйте ещё раз через минуту.';
  if (kind === 'network') return 'Нет связи с сервером. Проверьте интернет и нажмите ещё раз.';
  return 'Не получилось войти. Нажмите кнопку ещё раз.';
}

/** The server's public Telegram settings (GET /auth/telegram/config). */
export interface TelegramClientConfig {
  enabled: boolean;
  clientId: string | null;
}

/**
 * «Войти через Telegram» with everything around it: loads the server's
 * settings, says when Telegram Login is not configured or the server is
 * unreachable, and shows the error `onToken` resolves to.
 */
export function TelegramSignIn({
  loadConfig,
  getNonce,
  onToken,
  label,
}: {
  loadConfig: () => Promise<TelegramClientConfig>;
  getNonce: () => Promise<string>;
  /** Resolves to an error message, or null on success. */
  onToken: (idToken: string, nonce: string) => Promise<string | null>;
  label?: string;
}) {
  const [cfg, setCfg] = useState<TelegramClientConfig | null | 'error'>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useRef(loadConfig);
  const nonce = useRef(getNonce);
  const stableNonce = useCallback(() => nonce.current(), []);

  useEffect(() => {
    let alive = true;
    load
      .current()
      .then((c) => alive && setCfg(c))
      .catch(() => alive && setCfg('error'));
    return () => {
      alive = false;
    };
  }, []);

  if (cfg === null)
    return (
      <div className="h-14 animate-pulse rounded-xl bg-sunken" aria-busy aria-label="Загрузка" />
    );
  if (cfg === 'error')
    return (
      <p role="alert" className="rounded-xl bg-danger-soft px-4 py-3 text-base text-danger">
        Нет связи с сервером. Проверьте интернет и обновите страницу.
      </p>
    );
  if (!cfg.enabled || !cfg.clientId)
    return (
      <p role="status" className="rounded-xl bg-warning-soft px-4 py-3 text-base text-warning">
        Вход через Telegram ещё не настроен. Напишите в поддержку.
      </p>
    );
  return (
    <div className="space-y-3">
      <TelegramLoginButton
        clientId={cfg.clientId}
        getNonce={stableNonce}
        busy={busy}
        label={label}
        onToken={async (idToken, n) => {
          setBusy(true);
          setError(null);
          setError(await onToken(idToken, n));
          setBusy(false);
        }}
        onFailure={(kind) => setError(telegramFailureText(kind))}
      />
      {error && (
        <p role="alert" className="rounded-xl bg-danger-soft px-4 py-3 text-base text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
